import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import AtlasIcon from '../icons/AtlasIcon';
import CreateProjectModal from './BrowseTab/CreateProjectModal';
import NewSessionDialog from '../Session/NewSessionDialog';
import { applyImportNext, landCreatedBook } from '../DocumentImport/importNext';
import { storiesApi } from '../../api/stories';
import { useStoryNavigation } from '../../hooks/useStoryNavigation';
import {
  BOOK_MENU_REACH,
  SIDEBAR_MAP_GROUPS,
  SIDEBAR_SIDE_DOOR,
  SERIES_SIDEBAR_MAP_GROUPS,
  isBookNotesRouteActive,
  isChatSessionsPath,
  isDiscoveryPath,
  createSessionLaunch,
  isSeriesAltitude,
  isSeriesRoomPath,
} from '../Layout/sidebarMap';
import { isNotesPath, isProjectNotesPath, notesPathForAltitude } from '../../utils/seriesNotes';
import { seriesTimelinePath } from '../../utils/seriesLanding';
import { seriesCardsPath } from '../../utils/seriesSnapshots';
import { plottrackPathForBook } from '../../utils/plottrackPath';
import { styleCardsPathForBook } from '../../utils/styleCardsPath';
import { uploadBookId } from '../../utils/uploadBookId';
import { pathRoomPathForBook } from '../../utils/pathRoomPath';
import { navDisabledWhy, navWhyLine } from '../../utils/navDisabledWhy';
import { bookDiscoverySessionId, openBookDiscovery } from '../../utils/bookDiscoverySession';
import { runAfterLeaveGuard } from '../../utils/draftLeaveGuard';
import ImportUploadModal from '../DocumentImport/ImportUploadModal';
import './SidebarTabs.css';

/**
 * Mobile drawer map — same groups as desktop LeftNav (#653).
 * At series altitude the started-book list sits above the map (#684).
 */
const SidebarTabs = ({ onSessionSelect }) => {
  const routerNavigate = useNavigate();
  const navigate = (...args) => {
    runAfterLeaveGuard(() => routerNavigate(...args));
  };
  const location = useLocation();
  const {
    refreshProjects,
    setCurrentProject,
    setCurrentStory,
    currentProject,
    currentStory,
    currentSession,
    currentSessionId,
    stories,
    projects,
  } = useApp();
  const { switchStory } = useStoryNavigation();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showNewSession, setShowNewSession] = useState(false);
  const [createSessionCtx, setCreateSessionCtx] = useState(null);
  const [showUpload, setShowUpload] = useState(false);

  const resolvedDiscoveryId = bookDiscoverySessionId({
    currentStory,
    currentProject,
    stories,
  });
  const discoveryIds = [
    resolvedDiscoveryId,
    currentStory?.discovery_session_id,
    currentProject?.discovery_session_id,
  ].filter(Boolean);
  const activeSessionId = currentSessionId || currentSession?.id || null;
  const atSeriesAltitude = isSeriesAltitude({
    isSeries: Boolean(currentProject?.is_series),
    pathname: location.pathname,
    currentStory,
    currentSession,
  });
  const sessionLaunch = createSessionLaunch({
    atSeriesAltitude,
    currentProject,
    currentStory,
    currentSession,
  });
  const canCreateSession = Boolean(sessionLaunch?.storyId);
  const mapGroups = atSeriesAltitude ? SERIES_SIDEBAR_MAP_GROUPS : SIDEBAR_MAP_GROUPS;

  const openCreateSession = () => {
    if (!sessionLaunch?.storyId) return;
    setCreateSessionCtx(sessionLaunch);
    setShowNewSession(true);
  };

  const handleProjectCreated = async (newProject, options) => {
    await refreshProjects();
    setShowCreateModal(false);
    setCurrentProject(newProject);
    if (newProject?.story_id) {
      setCurrentStory({
        id: newProject.story_id,
        project_id: newProject.id,
        name: newProject.name,
        discovery_session_id: newProject.discovery_session_id || null,
      });
    }
    if (landCreatedBook(navigate, newProject, options)) return;
    applyImportNext(navigate, options, newProject, {
      currentStoryId: newProject?.story_id || currentStory?.id,
    });
  };

  const isActive = (item) => {
    if (item.id === 'discovery') {
      return isDiscoveryPath(location.pathname, activeSessionId, discoveryIds);
    }
    if (item.id === 'sessions') {
      return isChatSessionsPath(location.pathname, activeSessionId, discoveryIds);
    }
    if (item.id === 'notes') return isNotesPath(location.pathname);
    if (item.kind === 'timeline' || item.id === 'timeline') {
      return isSeriesRoomPath(location.pathname);
    }
    if (item.id === 'series-cards') {
      return location.pathname.includes('/series-cards')
        || location.pathname.includes('/series-offers')
        || location.pathname.includes('/series-dossiers');
    }
    if (item.bookNotesCategory || item.id === 'cards') {
      return isBookNotesRouteActive(item, location.pathname, location.search);
    }
    if (item.kind === 'route') {
      if (item.id === 'plot-notebook' || item.path === '/dossiers') {
        return location.pathname === '/dossiers' || location.pathname.startsWith('/dossiers/');
      }
      if (item.path === '/bibles') {
        return location.pathname.startsWith('/bibles');
      }
      if (item.id === 'path' || item.path === '/tasks' || item.path === '/path') {
        return location.pathname === '/tasks'
          || location.pathname.startsWith('/tasks/')
          || location.pathname === '/path'
          || location.pathname.startsWith('/path/');
      }
      return location.pathname.startsWith(item.path);
    }
    return false;
  };

  const openDiscovery = async () => {
    if (atSeriesAltitude && currentProject?.id) {
      setCurrentStory?.(null);
      if (currentProject.discovery_session_id) {
        navigate(`/chat/${currentProject.discovery_session_id}`);
        return;
      }
      navigate(`/projects/${currentProject.id}/discovery`);
      return;
    }
    try {
      await openBookDiscovery({
        currentStory,
        currentProject,
        stories,
        navigate,
        setCurrentStory,
        ensureDiscoverySession: storiesApi.ensureDiscoverySession,
      });
    } catch (error) {
      console.error('Failed to open Discovery:', error);
    }
  };

  const navWhyArgs = {
    hasStory: Boolean(currentStory),
    currentProject,
    currentStory,
    canCreateSession,
    projects,
    pathname: location.pathname,
  };

  const selectItem = (item) => {
    if (navDisabledWhy(item, navWhyArgs)) return;
    if (item.kind === 'create') {
      if (canCreateSession) openCreateSession();
      onSessionSelect?.();
      return;
    }
    if (atSeriesAltitude && item.id === 'sessions') {
      setCurrentStory?.(null);
    }
    if (item.kind === 'discovery') {
      openDiscovery();
      onSessionSelect?.();
      return;
    }
    if (item.kind === 'action' && item.id === 'upload') {
      if (currentProject) setShowUpload(true);
      return;
    }
    if (item.kind === 'notes') {
      if (currentProject?.id) {
        navigate(notesPathForAltitude({
          projectId: currentProject.id,
          storyId: currentStory?.id,
        }));
      }
      onSessionSelect?.();
      return;
    }
    if (item.kind === 'timeline') {
      if (currentProject?.id) {
        setCurrentStory?.(null);
        navigate(seriesTimelinePath(currentProject.id));
      }
      onSessionSelect?.();
      return;
    }
    if (item.kind === 'series-cards') {
      if (currentProject?.id) {
        setCurrentStory?.(null);
        navigate(seriesCardsPath(currentProject.id));
      }
      onSessionSelect?.();
      return;
    }
    if (item.id === 'plottrack') {
      // Same book-map door as desktop LeftNav (#849). item.path is
      // bare /plottrack; that first-painted last-in-series or most-recent.
      navigate(plottrackPathForBook({
        pathname: location.pathname,
        currentStory,
        currentSession,
      }));
      onSessionSelect?.();
      return;
    }
    if (item.id === 'path') {
      navigate(pathRoomPathForBook({
        pathname: location.pathname,
        currentStory,
        currentSession,
      }));
      onSessionSelect?.();
      return;
    }
    if (item.id === 'style-cards') {
      const path = styleCardsPathForBook({
        pathname: location.pathname,
        currentStory,
        currentSession,
      });
      if (path) navigate(path);
      onSessionSelect?.();
      return;
    }
    if (item.path) navigate(item.path);
    onSessionSelect?.();
  };

  const renderItem = (item) => {
    const why = navDisabledWhy(item, navWhyArgs);
    const whyLine = navWhyLine(item, navWhyArgs);
    const disabled = Boolean(why);
    return (
      <span className="sidebar-tabs__item-wrap" key={item.id}>
        <button
          type="button"
          className={[
            'sidebar-tabs__develop-link',
            isActive(item) ? 'is-active' : '',
            item.prominent && !disabled ? 'is-create' : '',
            disabled ? 'is-disabled' : '',
          ].filter(Boolean).join(' ')}
          disabled={disabled}
          aria-current={isActive(item) ? 'page' : undefined}
          title={whyLine || undefined}
          onClick={() => selectItem(item)}
        >
          <AtlasIcon name={item.icon} size={18} />
          <span>{item.label}</span>
        </button>
        {whyLine && <span className="sidebar-tabs__item-why">{whyLine}</span>}
      </span>
    );
  };

  const jumpToBook = (story) => {
    if (!currentProject) return;
    switchStory(currentProject, story);
    onSessionSelect?.();
  };

  return (
    <div className="sidebar-tabs sidebar-tabs--unified">
      <nav className="sidebar-tabs__map" aria-label="Project map">
        {atSeriesAltitude && (
          <div className="sidebar-tabs__books" role="listbox" aria-label="Series books">
            <p className="sidebar-tabs__group-label">Books in this series</p>
            {(stories || []).map((s) => (
              <button
                key={s.id}
                type="button"
                className="sidebar-tabs__develop-link"
                onClick={() => jumpToBook(s)}
              >
                <span>{s.name}</span>
              </button>
            ))}
            {(!stories || stories.length === 0) && (
              <p className="sidebar-tabs__books-empty">No books started yet</p>
            )}
          </div>
        )}
        {mapGroups.map((group) => (
          <div key={group.id} className="sidebar-tabs__group">
            <p className="sidebar-tabs__group-label">{group.label}</p>
            {group.items.map((item) => (
              <React.Fragment key={item.id}>
                {renderItem(item)}
                {item.children?.map(renderItem)}
              </React.Fragment>
            ))}
          </div>
        ))}
        {!atSeriesAltitude && (
          <div className="sidebar-tabs__group">
            <p className="sidebar-tabs__group-label">Also in this book</p>
            {BOOK_MENU_REACH.map((item) => (
              <React.Fragment key={item.id}>{renderItem(item)}</React.Fragment>
            ))}
          </div>
        )}
        <div className="sidebar-tabs__side-door">
          {renderItem(SIDEBAR_SIDE_DOOR)}
        </div>
      </nav>

      {showCreateModal && (
        <CreateProjectModal
          onClose={() => setShowCreateModal(false)}
          onCreated={handleProjectCreated}
        />
      )}
      {showNewSession && createSessionCtx?.storyId && (
        <NewSessionDialog
          storyId={createSessionCtx.storyId}
          projectId={createSessionCtx.projectId}
          storyName={
            (currentStory?.id === createSessionCtx.storyId && currentStory?.name)
              || (stories || []).find((s) => s.id === createSessionCtx.storyId)?.name
              || ''
          }
          seriesAltitude={false}
          onClose={() => {
            setShowNewSession(false);
            setCreateSessionCtx(null);
          }}
          onSessionCreated={(session) => {
            if (session?.id) navigate(`/chat/${session.id}`);
          }}
        />
      )}
      {showUpload && currentProject && (
        <ImportUploadModal
          project={currentProject}
          storyId={uploadBookId({
            pathname: location.pathname,
            currentStory,
            currentSession,
            atSeriesAltitude,
            onProjectNotes: isProjectNotesPath(location.pathname),
          })}
          showPromptChooser
          onClose={(payload) => {
            setShowUpload(false);
            applyImportNext(navigate, payload, currentProject, {
              currentStoryId: currentStory?.id,
            });
          }}
        />
      )}
    </div>
  );
};

export default SidebarTabs;
